import { useSelector } from 'react-redux';
import { usePermissions } from '../hooks/usePermissions';
import { TAB_ROUTES, type TabRoute } from './routes';
import { MIN_PLANS_FOR_ACCOUNT_CHAT } from '../constants/accountChat';
import type { RootState } from '../store';

// Resolve a tab's GLOBAL feature flag against the redux slice. Defaults to
// TRUE when the slice hasn't hydrated yet (preserves current behavior on
// cold boot — matches the migration's NOT NULL DEFAULT TRUE for shipped
// surfaces).
function tabFlagAllowed(route: TabRoute, flags: Record<string, boolean>, fetched: boolean): boolean {
  if (!route.featureFlag) return true;
  if (!fetched) return true;
  const value = flags?.[route.featureFlag];
  return typeof value === 'boolean' ? value : true;
}

/**
 * Mobile equivalent of the web app's useRoutes hook.
 *
 * Mirrors the same filter logic from the web's private_routes.filter():
 *
 *   1. If route has a condition() function, its return value determines visibility.
 *   2. If the account is a partner account:
 *        route.partner && hasAccountPermissions  (or always if no permissions required)
 *   3. For regular users (route.user === true):
 *        - is_project routes check project permissions (not applicable for tabs)
 *        - account routes check account permissions
 *   4. Fallback: hide.
 *
 * Account-level permissions come from state.session.account.permissions.
 * Partner flag comes from state.session.account.partner.
 */
export const useRoutes = (): { tabs: TabRoute[] } => {
  const authorized = useSelector((state: RootState) => state.session.authorized);
  const account = useSelector((state: RootState) => state.session.account);
  const projectsFetched = useSelector((state: RootState) => state.projects.fetched);
  const projectsTotal = useSelector((state: RootState) => state.projects.total);
  const featureFlags = useSelector((state: RootState) => state.featureFlags.flags);
  const featureFlagsFetched = useSelector((state: RootState) => state.featureFlags.fetched);
  const { hasAnyAccount } = usePermissions();

  if (!authorized) return { tabs: [] };

  const isPartner = !!account?.partner;

  // Account-level AI tab: only show when the user has account chat permission AND
  // enough plans for a cross-plan assistant to be meaningful. The cross-plan view
  // only earns its place once a caregiver is juggling several plans, so it's gated
  // on MIN_PLANS_FOR_ACCOUNT_CHAT regardless of paid status. Wait for the projects
  // fetch to complete before deciding so we don't flash the tab for users who
  // haven't loaded yet.
  const canUseAccountChat = hasAnyAccount('basic-user', 'account-chat');
  const hasEnoughPlans = projectsFetched && projectsTotal >= MIN_PLANS_FOR_ACCOUNT_CHAT;
  const showAITab = canUseAccountChat && hasEnoughPlans;

  // Account Home is only the first-run page now: at ZERO plans it carries the
  // "set up your first plan" flow, and there is nowhere else to send anyone.
  // With any plan it goes. It used to stay at two or more as the cross-plan
  // overview, but Zak removed that on 2026-10-06: each plan's items moved
  // into its Activity, and the plan menu lists every plan.
  //
  // Removing it lands the user on Plans, which is the initial route with a
  // plan (MainTabNavigator) and opens the plan last open (the Plans root).
  //
  // Defaults to SHOWN before the projects fetch resolves, as it always has:
  // the zero-plan account needs it, and a user with plans moves on as soon as
  // the list arrives.
  //
  // Chat-first onboarding takes Home away at ZERO too: the new account lands on
  // Plans, whose root is the plan chat: it sets up the first plan there.
  const chatFirstOnboarding = featureFlagsFetched && featureFlags?.chat_first_onboarding_enabled === true;
  const showHomeTab = !projectsFetched || (projectsTotal === 0 && !chatFirstOnboarding);

  const tabs = TAB_ROUTES.filter((route) => {
    const routePermissions = route.permissions ?? [];

    // Global feature flag gate runs first — short-circuits every other check
    // so a paused feature disappears from the nav regardless of permissions.
    if (!tabFlagAllowed(route, featureFlags, featureFlagsFetched)) return false;

    // Account-level AI tab: show when user can use account chat AND has a plan
    if (route.name === 'AI') return showAITab && (route.partner ? isPartner : route.user);

    // Only ever SUBTRACTS Home — the permission/user/partner logic below still
    // decides the positive case, so this cannot grant the tab to anyone.
    if (route.name === 'Home' && !showHomeTab) return false;

    // Condition override — same as web
    if (route.condition) return route.condition();

    const hasAccountPermissions =
      !routePermissions.length || hasAnyAccount(...routePermissions);

    // Partner accounts follow partner flag
    if (isPartner) {
      return !!(route.partner) && hasAccountPermissions;
    }

    // Regular user accounts follow user flag
    if (route.user) {
      return hasAccountPermissions;
    }

    return false;
  });

  // If Plans is visible but AI was filtered out (e.g. account.permissions missing on
  // mobile), add AI so the orb appears on care plans page — but only when the user
  // has enough plans (MIN_PLANS_FOR_ACCOUNT_CHAT). This fallback must honor the same
  // threshold as showAITab above, otherwise it re-adds the tab for users with too
  // few plans and defeats the gate.
  const hasPlansTab = tabs.some((r) => r.name === 'Plans');
  const hasAI = tabs.some((r) => r.name === 'AI');
  if (hasPlansTab && !hasAI && hasEnoughPlans) {
    const aiRoute = TAB_ROUTES.find((r) => r.name === 'AI');
    if (aiRoute) {
      const plansIdx = tabs.findIndex((r) => r.name === 'Plans');
      const inserted = [...tabs.slice(0, plansIdx + 1), aiRoute, ...tabs.slice(plansIdx + 1)];
      return { tabs: inserted };
    }
  }

  // Always fall back to at least Settings so the app is never tab-less
  if (!tabs.length) {
    return {
      tabs: TAB_ROUTES.filter((r) => r.name === 'Settings'),
    };
  }

  return { tabs };
};
